import {
  Box,
  Link,
  Button,
  IconButton,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React, { useContext } from "react";
import { ScenarioMap, useMapContextStore } from "../../../context/MapContext";
import {
  SchoolTypeFull,
  SchoolTypeOptions,
} from "../../../editing_planning/data_requirements";
import { AddressSearchBar } from "../../landing/AddressSearchBar";
import { AssignedSchool } from "../../../types/data/dataTypes";
import { useRouter } from "next/router";
import { handleSchoolSelection } from "../map/eventHandlers";
import { ScenarioSelector } from "../map/config/ScenarioSelector";
import { ScenarioExplanationsModal } from "./ScenarioExplanationsModal";
import { PageContext } from "../../../context/PageContext";

export const AssignmentsTab: React.FC<{ mobile: boolean }> = ({ mobile }) => {
  const content = useContext(PageContext);
  const { assignments, scenario, level, setLevel } = useMapContextStore();
  const router = useRouter();
  const existingSchools = assignments?.schools?.["0"].assignments;
  const assignedSchoools: {
    [key in SchoolTypeOptions]?: AssignedSchool[];
  } = assignments?.schools?.[`${scenario}`].assignments;

  const assignedSchools: AssignedSchool[][] = Object.values(
    assignedSchoools ?? {},
  );
  // console.log(existingSchools, assignedSchools);
  const swappedSchools = assignedSchools.length
    ? assignedSchools.reduce((changes, current) => {
        if (typeof current[0] === "string") return changes;
        // If there were previously multiple schools then this is always marked as a change.
        if (
          existingSchools[current[0].sch_level].length > 1 &&
          scenario !== 0
        ) {
          changes[current[0].sch_level] = existingSchools[
            current[0].sch_level
          ].map((s) => s.sch_name);
          return changes;
        }
        if (
          current[0].sch_id !== existingSchools[current[0].sch_level][0].sch_id
        ) {
          changes[current[0].sch_level] =
            existingSchools[current[0].sch_level][0].sch_name;
        }
        return changes;
      }, {})
    : null;
  const handleSchoolClick = (school: AssignedSchool) => {
    setLevel(school.sch_level);
    handleSchoolSelection({ ...school, sch_level: school.sch_level });
    router.push("/map?mode=view");
  };
  return (
    <Box
      sx={{
        overflow: "auto",
      }}
    >
      <Link
        sx={{ fontSize: ".875rem" }}
        href="https://dcpsspecialed.wixsite.com/home/self-contained-feeder-patterns"
      >
        {content.specEdLinkText}
      </Link>
      <br></br>
      <br></br>
      <Typography sx={{ fontSize: ".875rem" }}>
        {content.assignmentSchoolPlacementText1}{" "}
        <Link
          href="https://enrolldcps.dc.gov/sites/dcpsenrollment/files/page_content/attachments/SY24-25%20DCPS%20Enrollment%20and%20Lottery%20Handbook.pdf"
          target="_blank"
        >
          DCPS Enrollment and Lottery Handbook (page 38)
        </Link>
        {"."}
        <br></br>
        <br></br>
      </Typography>
      {!mobile ? <AddressSearchBar routeToMap={false} /> : null}
      {mobile ? <ScenarioSelector dropdown={mobile} /> : null}
      {assignedSchools.length ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            gap: 1,
          }}
        >
          {scenario !== 0 ? <ScenarioExplanationsModal /> : null}
          <Typography sx={{ pt: 2 }} variant="h3">
            {content[ScenarioMap[scenario]]}
          </Typography>
        </Box>
      ) : null}
      <TableContainer>
        <Table>
          <tbody>
            {assignedSchools.length
              ? assignedSchools.map((assignedSchool, index) => {
                  if (typeof assignedSchool[0] === "string") return null;

                  return (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", xl: "row" },
                          alignItems: { xs: "start", xl: "center" },
                          justifyContent: "space-between",
                          py: 1,
                          px: 2,
                          gap: 1,
                          color: "#b0b0b0",
                          backgroundColor:
                            assignedSchool[0].sch_level === level
                              ? "#f0f0f0"
                              : "",
                        }}
                      >
                        <Typography
                          color="#202020"
                          noWrap
                          sx={{
                            width: "fit-content",
                            maxWidth: "38vw",
                            fontSize: ".875rem",
                          }}
                        >
                          {`${
                            SchoolTypeFull[assignedSchool[0].sch_level]
                          } School`}{" "}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "fit-content",
                            maxWidth: "75vw",
                          }}
                        >
                          {swappedSchools[assignedSchool[0].sch_level] ? (
                            <Tooltip
                              enterTouchDelay={0}
                              disableInteractive
                              title={`Formerly: ${
                                swappedSchools[assignedSchool[0].sch_level]
                              }`}
                              sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                            >
                              <Typography
                                color="#fff"
                                sx={{
                                  fontSize: ".875rem",
                                  backgroundColor: "#154d61",
                                  padding: "0.25em",
                                  borderRadius: "0.5em",
                                  marginRight: "0.25rem",
                                }}
                              >
                                {typeof swappedSchools[
                                  assignedSchool[0].sch_level
                                ] === "object" &&
                                !assignedSchools.some(
                                  (school) => school[0]?.sch_id === 4541,
                                )
                                  ? "Sunset Multiple Rights"
                                  : "Changed"}
                              </Typography>
                            </Tooltip>
                          ) : undefined}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {assignedSchool.map((school) => {
                              if (typeof school === "string") return null;

                              return (
                                <Box
                                  key={school.sch_id}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "end",
                                  }}
                                >
                                  <Button
                                    color="secondary"
                                    title={`${school.sch_name}`}
                                    variant={
                                      school.sch_level === level
                                        ? "contained"
                                        : "outlined"
                                    }
                                    onClick={() => {
                                      setLevel(school.sch_level);
                                    }}
                                  >
                                    <Typography
                                      noWrap
                                      sx={{
                                        fontSize: { xs: ".875rem", md: "1rem" },
                                      }}
                                    >
                                      {school.sch_name}
                                    </Typography>
                                  </Button>

                                  <IconButton
                                    color="secondary"
                                    onClick={() => handleSchoolClick(school)}
                                  >
                                    <ArrowForwardIcon></ArrowForwardIcon>
                                  </IconButton>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              : null}
          </tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
